import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { venturesApi } from "@/lib/api-client";
import { CATEGORY_META } from "@/lib/format";
import { toast } from "sonner";
import { Save, Landmark, ShoppingCart, Briefcase, LineChart, Coins } from "lucide-react";

const ICONS = {
  real_estate: Landmark,
  ecommerce: ShoppingCart,
  services: Briefcase,
  portfolio: LineChart,
};

// Category-specific field schemas
const SCHEMAS = {
  real_estate: [
    { key: "property_id", label: "Property ID / Parcel", placeholder: "APN 12345-678" },
    { key: "address", label: "Address", placeholder: "123 Main St, City, State" },
    { key: "tax_record_url", label: "Tax Record URL", placeholder: "https://county-assessor.gov/…" },
    { key: "mls_id", label: "MLS Listing ID (optional)", placeholder: "MLS# 55123" },
  ],
  ecommerce: [
    { key: "platform", label: "Platform", placeholder: "shopify / amazon / etsy" },
    { key: "store_url", label: "Store URL", placeholder: "https://mystore.com" },
    { key: "api_key_ref", label: "API Credentials (vault ref)", placeholder: "vault:shopify_mystore" },
    { key: "domain", label: "Custom Domain (optional)", placeholder: "mystore.com" },
  ],
  services: [
    { key: "website", label: "Website", placeholder: "https://firm.com" },
    { key: "crm", label: "CRM System", placeholder: "hubspot / salesforce / pipedrive" },
    { key: "portal_url", label: "Client Portal (optional)", placeholder: "https://portal.firm.com" },
  ],
  portfolio: [
    { key: "exchange", label: "Exchange / Broker", placeholder: "coinbase / fidelity / kraken" },
    { key: "wallet_addresses", label: "Wallet Addresses (comma-sep)", placeholder: "bc1q…,0xabc…" },
    { key: "exchange_api_key_ref", label: "Exchange API Key (vault ref)", placeholder: "vault:coinbase_readonly" },
    { key: "tickers", label: "Tickers (comma-sep)", placeholder: "VTI,BTC,ETH" },
  ],
};

export default function VentureDetailsModal({ venture, open, onOpenChange, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [taxState, setTaxState] = useState(venture?.tax_state || "");
  const [entityType, setEntityType] = useState(venture?.entity_type || "");
  const [meta, setMeta] = useState(venture?.metadata || {});
  const [mission, setMission] = useState(venture?.mission || "");
  const [email, setEmail] = useState(venture?.email || "");
  const [goalsText, setGoalsText] = useState((venture?.goals || []).join("\n"));
  const [accounts, setAccounts] = useState(venture?.accounts || []);
  const [fh, setFh] = useState(venture?.financial_health || {});
  const [tracker, setTracker] = useState({
    coingecko_id: venture?.coingecko_id || "",
    holdings: venture?.holdings ?? "",
    stock_symbol: venture?.stock_symbol || "",
    shares: venture?.shares ?? "",
  });

  if (!venture) return null;

  const setFhField = (k, v) => setFh((prev) => ({ ...prev, [k]: v === "" ? "" : Number(v) }));

  const catMeta = CATEGORY_META[venture.category] || {};
  const Icon = ICONS[venture.category] || Landmark;
  const fields = SCHEMAS[venture.category] || [];

  const setField = (k, v) => setMeta((m) => ({ ...m, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...meta };
      if (typeof clean.wallet_addresses === "string") clean.wallet_addresses = clean.wallet_addresses.split(",").map(s => s.trim()).filter(Boolean);
      if (typeof clean.tickers === "string") clean.tickers = clean.tickers.split(",").map(s => s.trim().toUpperCase()).filter(Boolean);
      const goals = goalsText.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 6);
      const cleanAccounts = accounts.filter(a => a.platform && (a.handle || a.url));
      const cleanFh = Object.fromEntries(
        Object.entries(fh).filter(([, v]) => v !== "" && !Number.isNaN(v)).map(([k, v]) => [k, Number(v)])
      );
      await venturesApi.update(venture.id, {
        metadata: clean, tax_state: taxState || null, entity_type: entityType || null,
        mission: mission || null, email: email.trim() || null, goals, accounts: cleanAccounts,
        financial_health: cleanFh,
        coingecko_id: tracker.coingecko_id.trim().toLowerCase() || null,
        holdings: tracker.holdings === "" ? null : Number(tracker.holdings),
        stock_symbol: tracker.stock_symbol.trim().toUpperCase() || null,
        shares: tracker.shares === "" ? null : Number(tracker.shares),
      });
      toast.success("Data intake saved");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const val = (k) => {
    const v = meta[k];
    if (Array.isArray(v)) return v.join(", ");
    return v || "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border border-white/15 text-white max-w-lg p-0 rounded-none max-h-[90vh] overflow-hidden flex flex-col" data-testid="venture-details-modal">
        <div className="p-5 border-b border-white/10" style={{ background: `linear-gradient(135deg, ${catMeta.color}15, transparent 60%)` }}>
          <div className="flex items-center gap-2 mb-2">
            <Icon size={16} style={{ color: catMeta.color }}/>
            <span className="uc-chip">DATA INTAKE · {catMeta.label?.toUpperCase()}</span>
          </div>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-white">{venture.name}</DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-5 overflow-y-auto uc-scroll flex-1 space-y-4">
          {/* Tax fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Tax State / Nexus</Label>
              <Input data-testid="venture-tax-state" value={taxState} onChange={(e) => setTaxState(e.target.value.toUpperCase().slice(0, 2))} placeholder="NY / DE / TX" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc"/>
            </div>
            <div>
              <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Entity Type</Label>
              <Input data-testid="venture-entity" value={entityType} onChange={(e) => setEntityType(e.target.value)} placeholder="LLC / S-Corp / Trust" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc"/>
            </div>
          </div>

          {/* Category-specific fields */}
          {fields.map((f) => (
            <div key={f.key}>
              <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">{f.label}</Label>
              <Input
                data-testid={`meta-${f.key}`}
                value={val(f.key)}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"
              />
            </div>
          ))}

          <div className="p-3 border border-amber-500/20 bg-amber-500/5 text-[11px] text-white/70">
            <strong className="text-amber-400 font-mono-uc uppercase text-[10px] tracking-widest">Agent Note:</strong>
            <span className="ml-1">Scout and Sentinel use these identifiers to enrich the Net Worth Pulse and the Daily Briefing via live web search + API calls.</span>
          </div>

          {/* Mission Snippet */}
          <div className="pt-4 border-t border-white/10">
            <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Venture Email</Label>
            <Input
              data-testid="venture-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ops@halo-apparel.com"
              className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm mb-3"
            />
            <div className="font-mono-uc text-[10px] text-white/40 -mt-2 mb-3">
              First-class inbox address for this venture — Creator uses it as the from/reply-to and Sentinel monitors it.
            </div>
            <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Mission Snippet</Label>
            <textarea
              data-testid="venture-mission"
              value={mission}
              onChange={(e) => setMission(e.target.value.slice(0, 240))}
              placeholder="One or two sentences on what this venture is aiming at right now."
              className="w-full h-16 rounded-none bg-[#121212] border border-white/10 p-2 text-sm text-white/85 uc-focus"
            />
            <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1 mt-3">Top Goals (one per line, ≤ 6)</Label>
            <textarea
              data-testid="venture-goals"
              value={goalsText}
              onChange={(e) => setGoalsText(e.target.value)}
              placeholder={"Hit $250k MRR by end of Q2\nCut CAC by 20%\nLaunch NY wholesale channel"}
              className="w-full h-20 rounded-none bg-[#121212] border border-white/10 p-2 text-xs text-white/85 uc-focus font-mono-uc"
            />
          </div>

          {/* Financial Health (Controller inputs) */}
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Coins size={12} className="text-teal-400"/>
              <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50">Financial Health · Controller</Label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Cash On Hand ($)</Label>
                <Input data-testid="fh-cash" type="number" value={fh.cash_on_hand ?? ""} onChange={(e) => setFhField("cash_on_hand", e.target.value)} placeholder="25000" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Budget Cap ($)</Label>
                <Input data-testid="fh-budget" type="number" value={fh.budget_cap ?? ""} onChange={(e) => setFhField("budget_cap", e.target.value)} placeholder="5000" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Monthly Revenue ($)</Label>
                <Input data-testid="fh-revenue" type="number" value={fh.monthly_revenue ?? ""} onChange={(e) => setFhField("monthly_revenue", e.target.value)} placeholder="12000" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Monthly Expenses ($)</Label>
                <Input data-testid="fh-expenses" type="number" value={fh.monthly_expenses ?? ""} onChange={(e) => setFhField("monthly_expenses", e.target.value)} placeholder="8000" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
              <div className="col-span-2">
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Estimated Tax Liability ($)</Label>
                <Input data-testid="fh-tax" type="number" value={fh.estimated_tax_liability ?? ""} onChange={(e) => setFhField("estimated_tax_liability", e.target.value)} placeholder="18000" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
            </div>
            <div className="text-[10px] text-white/40 mt-2 font-mono-uc">
              Controller uses these to gate Executor spend via the Liquidity Guard and to compute Net-of-Tax pulse.
            </div>
          </div>

          {/* Live Market Trackers */}
          <div className="pt-4 border-t border-white/10">
            <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-2">Live Market Trackers · auto-synced every 15 min</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">CoinGecko ID (crypto)</Label>
                <Input data-testid="tracker-coingecko" value={tracker.coingecko_id} onChange={(e) => setTracker({ ...tracker, coingecko_id: e.target.value })} placeholder="bitcoin / ethereum" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Holdings (units)</Label>
                <Input data-testid="tracker-holdings" type="number" value={tracker.holdings} onChange={(e) => setTracker({ ...tracker, holdings: e.target.value })} placeholder="1.25" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Stock Symbol</Label>
                <Input data-testid="tracker-symbol" value={tracker.stock_symbol} onChange={(e) => setTracker({ ...tracker, stock_symbol: e.target.value.toUpperCase() })} placeholder="SPY / AAPL" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
              <div>
                <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/40 block mb-1">Shares</Label>
                <Input data-testid="tracker-shares" type="number" value={tracker.shares} onChange={(e) => setTracker({ ...tracker, shares: e.target.value })} placeholder="640" className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc text-sm"/>
              </div>
            </div>
            <div className="text-[10px] text-white/40 mt-2 font-mono-uc">
              Set either (or both) — this slot's value then follows live CoinGecko / market prices automatically.
            </div>
          </div>

          {/* Linked Accounts */}
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50">Linked Accounts</Label>
              <button data-testid="add-account-btn" onClick={() => setAccounts([...accounts, { _key: `acc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, platform: "", handle: "", url: "" }])} className="uc-chip hover:bg-white/5 text-[10px]">+ Account</button>
            </div>
            <div className="space-y-2">
              {accounts.map((a, i) => (
                <div key={a._key || a.id || `${a.platform}-${a.handle}-${i}`} className="grid grid-cols-12 gap-1 items-center" data-testid={`account-${i}`}>
                  <Input data-testid={`account-platform-${i}`} value={a.platform || ""} onChange={(e) => setAccounts(accounts.map((x, ix) => ix === i ? { ...x, platform: e.target.value } : x))} placeholder="twitter" className="col-span-3 h-9 rounded-none bg-black border-white/10 text-xs font-mono-uc"/>
                  <Input data-testid={`account-handle-${i}`} value={a.handle || ""} onChange={(e) => setAccounts(accounts.map((x, ix) => ix === i ? { ...x, handle: e.target.value } : x))} placeholder="@handle" className="col-span-3 h-9 rounded-none bg-black border-white/10 text-xs font-mono-uc"/>
                  <Input data-testid={`account-url-${i}`} value={a.url || ""} onChange={(e) => setAccounts(accounts.map((x, ix) => ix === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className="col-span-5 h-9 rounded-none bg-black border-white/10 text-xs font-mono-uc"/>
                  <button data-testid={`account-remove-${i}`} onClick={() => setAccounts(accounts.filter((_, ix) => ix !== i))} className="col-span-1 h-9 border border-white/10 text-white/50 hover:text-red-400 flex items-center justify-center">×</button>
                </div>
              ))}
              {accounts.length === 0 && <div className="text-white/40 text-[11px] italic">Link email + social + platform accounts here so Creator can draft with the right voice.</div>}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-white/10 flex gap-2">
          <Button data-testid="venture-details-cancel" onClick={() => onOpenChange(false)} variant="ghost" className="rounded-none h-10 border border-white/10 text-white/70 flex-1">Cancel</Button>
          <Button data-testid="venture-details-save" onClick={save} disabled={busy} className="uc-btn-amber rounded-none h-10 flex-1">
            <Save size={12} className="mr-1"/> Save Intake
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
